import { useDebouncedValidate } from '@hooks/useDebouncedValidate';
import { normalizeValidationKey } from '@pages/payments/utils/bicUtils';
import apiClient from '@services/apiClient';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ValidateResult } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VALIDATION_GC_TIME } from './bicApi';

interface IbanValidationResponse {
	iban_structure_valid: boolean;
	iban_checksum_valid: boolean;
	bic: string | null;
}

interface IbanValidationResult {
	isValid: boolean;
	bicMask: string | null;
}

const validateIban = async (iban?: string): Promise<IbanValidationResult> => {
	if (!iban) {
		return { isValid: true, bicMask: null };
	}

	const response = await apiClient.post<IbanValidationResponse>(
		'/utilities/iban/validate',
		{
			includeDetails: true,
			iban,
		},
	);

	return response.data
		? {
				isValid: !response.data.iban_structure_valid || response.data.iban_checksum_valid,
				bicMask: response.data.bic,
			}
		: { isValid: true, bicMask: null };
};

const IBAN_FIELD_NAME = 'beneficiary.account';

type IbanFetchPlan = {
	queryKey: readonly unknown[];
	queryFn: () => Promise<IbanValidationResult>;
} | null;

interface UseIbanValidationCoreParams {
	planFetch: (normalized: string) => IbanFetchPlan;
}

const useIbanValidationCore = ({ planFetch }: UseIbanValidationCoreParams) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { clearErrors } = useFormContext();
	const [isValidating, setIsValidating] = useState(false);
	const [bicMask, setBicMask] = useState<string | null>(null);
	const latestIbanRef = useRef('');

	const rejectIban = (value: string): ValidateResult => {
		if (latestIbanRef.current !== value) return true;
		setBicMask(null);
		return t('payments.newPayment.form.accountNoError');
	};

	const fetchIban = async (value: string): Promise<ValidateResult> => {
		const plan = planFetch(normalizeValidationKey(value));
		if (!plan) {
			setBicMask(null);
			return true;
		}
		setIsValidating(true);
		try {
			const data = await queryClient.fetchQuery({
				queryKey: plan.queryKey,
				queryFn: plan.queryFn,
				staleTime: Infinity,
				gcTime: VALIDATION_GC_TIME,
			});

			if (latestIbanRef.current !== value) return true;
			if (!data.isValid) return rejectIban(value);

			setBicMask(data.bicMask);
			return true;
		} catch {
			return rejectIban(value);
		} finally {
			if (latestIbanRef.current === value) {
				setIsValidating(false);
			}
		}
	};
	const clearIbanError = useCallback(() => {
		clearErrors(IBAN_FIELD_NAME);
	}, [clearErrors]);
	const debouncedFetchIban = useDebouncedValidate(fetchIban, undefined, clearIbanError);

	const validate = async (value: string): Promise<ValidateResult> => {
		latestIbanRef.current = value;

		if (!value) {
			setBicMask(null);
			setIsValidating(false);
			return true;
		}
		return debouncedFetchIban(value);
	};

	const resetBicMask = useCallback(() => setBicMask(null), []);

	return { validate, isValidating, bicMask, resetBicMask };
};

export const useSwiftIbanValidations = () => {
	const { validate, isValidating, bicMask } = useIbanValidationCore({
		planFetch: (normalized) => ({
			queryKey: ['iban', normalized],
			queryFn: () => validateIban(normalized),
		}),
	});

	return { validate, isValidating, ibanBicMask: bicMask };
};

export const useFXIbanValidation = (isIbanField?: boolean) => {
	const { trigger, clearErrors, getValues, formState } = useFormContext();
	const paymentType = useWatch({ name: 'fx_remittance_type' });
	const fromCurrency = useWatch({ name: 'currency' });
	const toCurrency = useWatch({ name: 'fx_currency' });
	const ibanValue = useWatch({ name: 'beneficiary.account' });

	const { validate, isValidating, bicMask, resetBicMask } = useIbanValidationCore({
		planFetch: (normalized) => ({
			queryKey: ['iban', normalized],
			queryFn: () => validateIban(normalized),
		}),
	});

	useEffect(() => {
		if (!isIbanField) {
			return;
		}

		const isTouched = formState.touchedFields?.beneficiary?.account;
		const currentValue = getValues('beneficiary.account');
		if (currentValue || isTouched) {
			const timeoutId = setTimeout(() => {
				clearErrors('beneficiary.account');
				trigger('beneficiary.account');
			}, 300);
			return () => clearTimeout(timeoutId);
		}
	}, [paymentType, fromCurrency, toCurrency]);

	useEffect(() => {
		if (isIbanField && !ibanValue) {
			resetBicMask();
		}
	}, [isIbanField, ibanValue, resetBicMask]);

	return { validate, isValidating, bicMask };
};
